import CloseOutlined from "@ant-design/icons/CloseOutlined"
import ReloadOutlined from "@ant-design/icons/ReloadOutlined"
import { type ReactElement, useCallback, useEffect, useRef, useState } from "react"

import { isEmbed } from "@/util/embed"
import { Feature, getFeatureNumber } from "@/util/gating"
import { FRONTEND_BUILD_SHA, FRONTEND_COMMITTED_AT, fetchVersion } from "@/util/version"

import "./StaleVersionBanner.less"

const POLL_INTERVAL_MS = 5 * 60 * 1000 // 5 minutes

// The banner is shown only when the live build is on a different commit and
// its commit time is at least `thresholdMinutes` newer than the loaded bundle.
// If we cannot confirm that gap because either timestamp is missing or
// unparseable, we stay quiet.
function isServerNewerByThreshold(
  serverCommittedAt: string,
  thresholdMinutes: number,
): boolean {
  if (!FRONTEND_COMMITTED_AT) {
    return false
  }
  const serverTime = new Date(serverCommittedAt).getTime()
  const frontendTime = new Date(FRONTEND_COMMITTED_AT).getTime()
  if (Number.isNaN(serverTime) || Number.isNaN(frontendTime)) {
    return false
  }
  const deltaMinutes = (serverTime - frontendTime) / 60_000
  return deltaMinutes >= thresholdMinutes
}

export function StaleVersionBanner(): ReactElement | null {
  const [stale, setStale] = useState(false)
  const [dismissed, setDismissed] = useState(false)
  const thresholdRef = useRef<number | undefined>(
    getFeatureNumber(Feature.StaleVersionBannerThresholdMinutes),
  )

  const checkVersion = useCallback(async () => {
    const threshold = thresholdRef.current
    if (threshold === undefined) {
      return
    }

    const info = await fetchVersion()
    if (!info?.build_sha) {
      return
    }

    if (info.build_sha === FRONTEND_BUILD_SHA) {
      return
    }

    if (!info.build_committed_at) {
      return
    }

    if (!isServerNewerByThreshold(info.build_committed_at, threshold)) {
      return
    }

    setStale(true)
  }, [])

  const embed = isEmbed()
  const disabled = thresholdRef.current === undefined

  useEffect(() => {
    if (embed || disabled) {
      return undefined
    }

    void checkVersion()

    const interval = setInterval(() => void checkVersion(), POLL_INTERVAL_MS)

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void checkVersion()
      }
    }
    document.addEventListener("visibilitychange", onVisibilityChange)

    return () => {
      clearInterval(interval)
      document.removeEventListener("visibilitychange", onVisibilityChange)
    }
  }, [checkVersion, embed, disabled])

  if (!stale || dismissed || embed || disabled) {
    return null
  }

  return (
    <div className="StaleVersionBanner">
      <span className="StaleVersionBanner__label">A new version is available.</span>
      <button
        type="button"
        className="StaleVersionBanner__button"
        onClick={() => window.location.reload()}
      >
        <ReloadOutlined />
        <span>Refresh to update</span>
      </button>
      <button
        type="button"
        className="StaleVersionBanner__close"
        aria-label="Dismiss"
        onClick={() => setDismissed(true)}
      >
        <CloseOutlined />
      </button>
    </div>
  )
}
